import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { Phone, Mail, MessageCircle, Download, LogOut, Search, X } from "lucide-react";
import { SITE } from "@/data/content";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUSES = ["NEW", "CONTACTED", "FOLLOW-UP", "SITE VISIT", "QUALIFIED", "CONVERTED", "LOST"];

const STATUS_STYLES = {
  NEW: "bg-brand/15 text-brand-dark",
  CONTACTED: "bg-ink/10 text-ink",
  "FOLLOW-UP": "bg-[#B45309]/15 text-[#B45309]",
  "SITE VISIT": "bg-[#1D4ED8]/10 text-[#1D4ED8]",
  QUALIFIED: "bg-[#0F766E]/10 text-[#0F766E]",
  CONVERTED: "bg-[#15803D]/10 text-[#15803D]",
  LOST: "bg-[#B91C1C]/10 text-[#B91C1C]",
};

const waNumber = (phone) => phone.replace(/[^0-9]/g, "");

const formatDate = (iso) => {
  try {
    return new Date(iso).toLocaleString("en-IN", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" });
  } catch {
    return iso;
  }
};

const Admin = () => {
  const [token, setToken] = useState(() => sessionStorage.getItem("evaya_admin_token") || "");
  const [loginForm, setLoginForm] = useState({ email: "", password: "" });
  const [loginError, setLoginError] = useState("");
  const [loggingIn, setLoggingIn] = useState(false);
  const [leads, setLeads] = useState(null);
  const [search, setSearch] = useState("");
  const [selected, setSelected] = useState(null);
  const [notesDraft, setNotesDraft] = useState("");
  const [notesSaved, setNotesSaved] = useState(false);

  const openLead = (lead) => {
    setSelected(lead);
    setNotesDraft(lead.notes || "");
    setNotesSaved(false);
  };

  const saveNotes = async () => {
    await axios.patch(
      `${API}/admin/leads/${selected.id}`,
      { notes: notesDraft },
      { headers: { Authorization: `Bearer ${token}` } }
    );
    setLeads((prev) => prev.map((l) => (l.id === selected.id ? { ...l, notes: notesDraft } : l)));
    setSelected((prev) => (prev ? { ...prev, notes: notesDraft } : prev));
    setNotesSaved(true);
  };

  const loadLeads = useCallback(
    async (q = "") => {
      try {
        const res = await axios.get(`${API}/admin/leads`, {
          params: { search: q },
          headers: { Authorization: `Bearer ${token}` },
        });
        setLeads(res.data.leads);
      } catch (err) {
        if (err?.response?.status === 401) {
          sessionStorage.removeItem("evaya_admin_token");
          setToken("");
        }
      }
    },
    [token]
  );

  useEffect(() => {
    if (token) loadLeads();
  }, [token, loadLeads]);

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoggingIn(true);
    setLoginError("");
    try {
      const res = await axios.post(`${API}/admin/login`, loginForm);
      sessionStorage.setItem("evaya_admin_token", res.data.token);
      setToken(res.data.token);
    } catch {
      setLoginError("Invalid credentials. Please try again.");
    } finally {
      setLoggingIn(false);
    }
  };

  const handleLogout = () => {
    sessionStorage.removeItem("evaya_admin_token");
    setToken("");
    setLeads(null);
    setSelected(null);
  };

  const updateStatus = async (leadId, status) => {
    await axios.patch(
      `${API}/admin/leads/${leadId}`,
      { status },
      { headers: { Authorization: `Bearer ${token}` } }
    );
    setLeads((prev) => prev.map((l) => (l.id === leadId ? { ...l, status } : l)));
    setSelected((prev) => (prev && prev.id === leadId ? { ...prev, status } : prev));
  };

  const exportCsv = () => {
    const header = ["Date", "Name", "Phone", "Email", "Enquiry Type", "Message", "Status", "Notes"];
    const esc = (v) => `"${String(v ?? "").replace(/"/g, '""')}"`;
    const rows = leads.map((l) =>
      [formatDate(l.created_at), l.name, l.phone, l.email, l.requirement, l.message, l.status, l.notes || ""].map(esc).join(",")
    );
    const blob = new Blob([[header.map(esc).join(","), ...rows].join("\n")], { type: "text/csv" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = `evaya-leads-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(a.href);
  };

  if (!token) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-ink px-6" data-testid="admin-login-page">
        <form onSubmit={handleLogin} className="w-full max-w-md border border-white/10 bg-white p-8 lg:p-10" data-testid="admin-login-form">
          <img src={SITE.logo} alt="Evaya Ventures" className="h-16 w-auto" />
          <h1 className="mt-6 font-display text-3xl text-ink">Lead Dashboard</h1>
          <p className="mt-2 text-sm text-ink/60">Authorised access only.</p>
          <label htmlFor="admin-email" className="mt-8 block text-xs font-semibold uppercase tracking-[0.2em] text-ink/60">Email</label>
          <input
            id="admin-email"
            type="email"
            required
            value={loginForm.email}
            onChange={(e) => setLoginForm((f) => ({ ...f, email: e.target.value }))}
            className="mt-2 w-full rounded-sm border border-ink/20 px-4 py-3 text-sm focus:border-brand focus:outline-none focus:ring-2 focus:ring-brand/30"
            data-testid="admin-email-input"
          />
          <label htmlFor="admin-password" className="mt-5 block text-xs font-semibold uppercase tracking-[0.2em] text-ink/60">Password</label>
          <input
            id="admin-password"
            type="password"
            required
            value={loginForm.password}
            onChange={(e) => setLoginForm((f) => ({ ...f, password: e.target.value }))}
            className="mt-2 w-full rounded-sm border border-ink/20 px-4 py-3 text-sm focus:border-brand focus:outline-none focus:ring-2 focus:ring-brand/30"
            data-testid="admin-password-input"
          />
          {loginError && <p className="mt-4 text-sm text-[#B91C1C]" data-testid="admin-login-error">{loginError}</p>}
          <button
            type="submit"
            disabled={loggingIn}
            className="mt-7 w-full rounded-sm bg-brand px-6 py-3.5 text-sm font-semibold tracking-wide text-ink transition-colors duration-300 hover:bg-brand-light disabled:opacity-60"
            data-testid="admin-login-button"
          >
            {loggingIn ? "Signing in..." : "Sign In"}
          </button>
        </form>
      </div>
    );
  }

  const counts = STATUSES.reduce((acc, s) => ({ ...acc, [s]: (leads || []).filter((l) => l.status === s).length }), {});

  return (
    <div className="min-h-screen bg-sand" data-testid="admin-dashboard">
      <div className="border-b border-white/10 bg-ink">
        <div className="mx-auto flex max-w-7xl items-center justify-between px-6 py-5 lg:px-12">
          <div className="flex items-center gap-4">
            <img src={SITE.logo} alt="Evaya Ventures" className="h-12 w-auto" />
            <div>
              <p className="text-[10px] font-semibold uppercase tracking-[0.3em] text-brand-light">Evaya Ventures</p>
              <h1 className="font-display text-2xl text-white" data-testid="admin-dashboard-title">Lead Dashboard</h1>
            </div>
          </div>
          <button
            onClick={handleLogout}
            className="flex items-center gap-2 rounded-sm border border-white/20 px-4 py-2 text-xs font-medium text-white/80 transition-colors duration-300 hover:border-brand hover:text-brand-light"
            data-testid="admin-logout-button"
          >
            <LogOut className="h-4 w-4" strokeWidth={1.5} />
            Logout
          </button>
        </div>
      </div>

      <div className="mx-auto max-w-7xl px-6 py-10 lg:px-12">
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-4 lg:grid-cols-8" data-testid="admin-stats">
          <div className="border border-ink/10 bg-white p-4">
            <p className="text-[10px] font-semibold uppercase tracking-[0.18em] text-ink/50">Total Leads</p>
            <p className="mt-2 font-display text-3xl text-ink" data-testid="stat-total">{leads ? leads.length : "—"}</p>
          </div>
          {STATUSES.map((s) => (
            <div key={s} className="border border-ink/10 bg-white p-4">
              <p className="text-[10px] font-semibold uppercase tracking-[0.18em] text-ink/50">{s}</p>
              <p className="mt-2 font-display text-3xl text-ink" data-testid={`stat-${s.toLowerCase().replace(/\s+/g, "-")}`}>{leads ? counts[s] : "—"}</p>
            </div>
          ))}
        </div>

        <div className="mt-8 flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
          <div className="relative w-full max-w-md">
            <Search className="absolute left-4 top-1/2 h-4 w-4 -translate-y-1/2 text-ink/40" strokeWidth={1.5} />
            <input
              type="text"
              value={search}
              onChange={(e) => {
                setSearch(e.target.value);
                loadLeads(e.target.value);
              }}
              placeholder="Search name, phone or email"
              className="w-full rounded-sm border border-ink/20 bg-white py-3 pl-11 pr-4 text-sm focus:border-brand focus:outline-none focus:ring-2 focus:ring-brand/30"
              data-testid="admin-search-input"
            />
          </div>
          <button
            onClick={exportCsv}
            disabled={!leads || leads.length === 0}
            className="flex items-center justify-center gap-2 rounded-sm border border-ink/25 px-5 py-3 text-sm font-medium text-ink transition-colors duration-300 hover:bg-ink hover:text-white disabled:opacity-50"
            data-testid="admin-export-csv-button"
          >
            <Download className="h-4 w-4" strokeWidth={1.5} />
            Export CSV
          </button>
        </div>

        <div className="mt-6 overflow-x-auto border border-ink/10 bg-white" data-testid="admin-leads-table">
          <table className="w-full min-w-[900px] text-left text-sm">
            <thead>
              <tr className="border-b border-ink/10 text-[11px] uppercase tracking-[0.18em] text-ink/50">
                <th className="px-5 py-4 font-semibold">Date</th>
                <th className="px-5 py-4 font-semibold">Name</th>
                <th className="px-5 py-4 font-semibold">Phone</th>
                <th className="px-5 py-4 font-semibold">Email</th>
                <th className="px-5 py-4 font-semibold">Enquiry Type</th>
                <th className="px-5 py-4 font-semibold">Status</th>
                <th className="px-5 py-4 font-semibold">Actions</th>
              </tr>
            </thead>
            <tbody>
              {(leads || []).map((lead) => (
                <tr key={lead.id} className="border-b border-ink/5 transition-colors duration-200 hover:bg-sand/60" data-testid={`lead-row-${lead.id}`}>
                  <td className="whitespace-nowrap px-5 py-4 text-ink/60">{formatDate(lead.created_at)}</td>
                  <td className="px-5 py-4 font-medium text-ink">{lead.name}</td>
                  <td className="whitespace-nowrap px-5 py-4 text-ink/70">{lead.phone}</td>
                  <td className="px-5 py-4 text-ink/70">{lead.email}</td>
                  <td className="px-5 py-4 text-ink/70">{lead.requirement}</td>
                  <td className="px-5 py-4">
                    <span className={`inline-block rounded-sm px-2.5 py-1 text-[11px] font-semibold tracking-wide ${STATUS_STYLES[lead.status] || STATUS_STYLES.NEW}`} data-testid={`lead-status-${lead.id}`}>
                      {lead.status}
                    </span>
                  </td>
                  <td className="px-5 py-4">
                    <button
                      onClick={() => openLead(lead)}
                      className="rounded-sm border border-ink/20 px-4 py-1.5 text-xs font-semibold text-ink transition-colors duration-300 hover:bg-ink hover:text-white"
                      data-testid={`lead-view-${lead.id}`}
                    >
                      View
                    </button>
                  </td>
                </tr>
              ))}
              {leads && leads.length === 0 && (
                <tr>
                  <td colSpan={7} className="px-5 py-12 text-center text-ink/50" data-testid="leads-empty">
                    No leads found.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {selected && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-ink/70 p-4" data-testid="lead-detail-modal" onClick={() => setSelected(null)}>
          <div className="max-h-[90vh] w-full max-w-2xl overflow-y-auto bg-white p-8 lg:p-10" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-start justify-between gap-4">
              <div>
                <p className="text-[11px] font-semibold uppercase tracking-[0.24em] text-brand-dark">{selected.requirement}</p>
                <h2 className="mt-2 font-display text-3xl text-ink" data-testid="lead-detail-name">{selected.name}</h2>
                <p className="mt-1 text-sm text-ink/50">{formatDate(selected.created_at)}</p>
              </div>
              <button onClick={() => setSelected(null)} aria-label="Close lead details" className="rounded-sm border border-ink/20 p-2 text-ink transition-colors duration-300 hover:bg-ink hover:text-white" data-testid="lead-detail-close">
                <X className="h-4 w-4" strokeWidth={1.5} />
              </button>
            </div>

            <dl className="mt-8 space-y-4 text-sm">
              <div className="flex gap-4"><dt className="w-24 shrink-0 text-ink/50">Phone</dt><dd className="font-medium text-ink" data-testid="lead-detail-phone">{selected.phone}</dd></div>
              <div className="flex gap-4"><dt className="w-24 shrink-0 text-ink/50">Email</dt><dd className="font-medium text-ink" data-testid="lead-detail-email">{selected.email}</dd></div>
              <div className="flex gap-4"><dt className="w-24 shrink-0 text-ink/50">Message</dt><dd className="leading-relaxed text-ink/80" data-testid="lead-detail-message">{selected.message}</dd></div>
            </dl>

            <div className="mt-8">
              <label htmlFor="lead-status-select" className="text-[11px] font-semibold uppercase tracking-[0.2em] text-ink/50">Status</label>
              <select
                id="lead-status-select"
                value={selected.status}
                onChange={(e) => updateStatus(selected.id, e.target.value)}
                className="mt-2 w-full rounded-sm border border-ink/20 bg-white px-4 py-3 text-sm focus:border-brand focus:outline-none focus:ring-2 focus:ring-brand/30"
                data-testid="lead-status-select"
              >
                {STATUSES.map((s) => (
                  <option key={s} value={s}>{s}</option>
                ))}
              </select>
            </div>

            <div className="mt-6">
              <label htmlFor="lead-notes-input" className="text-[11px] font-semibold uppercase tracking-[0.2em] text-ink/50">Notes</label>
              <textarea
                id="lead-notes-input"
                rows={4}
                value={notesDraft}
                onChange={(e) => {
                  setNotesDraft(e.target.value);
                  setNotesSaved(false);
                }}
                placeholder="Record call outcomes, follow-up details, site visit notes..."
                className="mt-2 w-full resize-y rounded-sm border border-ink/20 bg-white px-4 py-3 text-sm focus:border-brand focus:outline-none focus:ring-2 focus:ring-brand/30"
                data-testid="lead-notes-input"
              />
              <div className="mt-3 flex items-center gap-3">
                <button
                  onClick={saveNotes}
                  className="rounded-sm bg-brand px-5 py-2.5 text-xs font-semibold text-ink transition-colors duration-300 hover:bg-brand-light"
                  data-testid="lead-notes-save-button"
                >
                  Save Notes
                </button>
                {notesSaved && (
                  <span className="text-xs font-medium text-[#15803D]" data-testid="lead-notes-saved">
                    Notes saved
                  </span>
                )}
              </div>
            </div>

            <div className="mt-8 flex flex-wrap gap-3">
              <a href={`tel:${waNumber(selected.phone)}`} className="flex items-center gap-2 rounded-sm bg-ink px-5 py-3 text-xs font-semibold text-white transition-colors duration-300 hover:bg-olive" data-testid="lead-call-button">
                <Phone className="h-4 w-4" strokeWidth={1.5} /> Call
              </a>
              <a href={`mailto:${selected.email}`} className="flex items-center gap-2 rounded-sm bg-ink px-5 py-3 text-xs font-semibold text-white transition-colors duration-300 hover:bg-olive" data-testid="lead-email-button">
                <Mail className="h-4 w-4" strokeWidth={1.5} /> Email
              </a>
              <a href={`https://wa.me/${waNumber(selected.phone)}`} target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 rounded-sm bg-[#25D366] px-5 py-3 text-xs font-semibold text-white transition-opacity duration-300 hover:opacity-90" data-testid="lead-whatsapp-button">
                <MessageCircle className="h-4 w-4" strokeWidth={1.5} /> WhatsApp
              </a>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Admin;
